import { get, isEmpty } from 'lodash'
import moment from 'moment'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { setPatientProfile } from 'src/store/modules/actionCreators'
import {
  getPatientProfile,
  patientUuidSelector,
  removeDeviceId,
  setAddresses,
  setAssignedCaregivers,
  setAvailableCaregivers,
  setPatientAlertNote,
  setPatientAllergies,
  setPatientEvents
} from 'src/store/modules/patient'

import {
  ASSIGNED_CAREGIVER_DELETE_MESSAGE,
  ASSIGNED_CAREGIVER_SAVE_MESSAGE,
  ASSIGNED_CAREGIVER_UPDATE_MESSAGE,
  DATE_TIME_FORMAT_FOR_SERVER,
  DELETE_ASSIGNED_CAREGIVER,
  DELETE_PATIENT_ALERT_NOTE,
  GET_ASSIGNED_CAREGIVERS,
  GET_AVAILABLE_CAREGIVER_LIST,
  GET_PATIENT_ALERT_NOTE,
  PATCH_PATIENT_DEMOGRAPHICS_COMPANY_DIVISION,
  PATIENT_DEMOGRAPHICS_ADDRESSES,
  PATIENT_DEMOGRAPHICS_ALLERGIES,
  PATIENT_DEMOGRAPHICS_EVENT,
  PATIENT_DEMOGRAPHICS_EVENTS,
  PATIENT_DEMOGRAPHICS_GET_FACESHEET_REPORT,
  PATIENT_DEMOGRAPHICS_GET_FACESHEET_REPORT_BATCH,
  PATIENT_REFERRALS,
  POST_PATCH_ASSIGNED_CAREGIVER,
  POST_PATIENT_GENERATE_PHONE,
  REPORT_SUCCESS_UPLOAD_MESSAGE,
  SAVE_PATIENT_ALERT_NOTE,
  UPDATE_PATIENT_ALERT_NOTE,
  UPDATE_PATIENT_SPECIAL_FIELDS
} from 'src/constants'
import { showSuccessModal } from 'src/services/dialogs'
import { exportReport } from 'src/utils/Functions'

export const fetchPatientAddresses =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_ADDRESSES,
          ...config
        }
      })

      dispatch(setAddresses(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchPatientAllergies =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_ALLERGIES,
          ...config
        }
      })

      dispatch(setPatientAllergies(items))
    } catch (e) {
      catchError(e)
    }
  }

export const fetchPatientEvents =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_EVENTS,
          ...config
        }
      })

      dispatch(setPatientEvents(items))
    } catch (e) {
      catchError(e)
    }
  }

export const postPatientEvent =
  (config = {}) =>
  async () => {
    try {
      const { payload = {}, params = {} } = config

      const event = await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_EVENTS,
          params
        },
        payload
      })

      return event
    } catch (e) {
      catchError(e)
    }
  }

export const updatePatientEvent =
  (config = {}) =>
  async () => {
    try {
      const { payload = {}, params = {} } = config

      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_EVENT,
          params
        },
        payload
      })
    } catch (e) {
      catchError(e)
    }
  }

export const deletePatientEvent =
  (config = {}) =>
  async () => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: PATIENT_DEMOGRAPHICS_EVENT,
          ...config
        }
      })
    } catch (e) {
      catchError(e)
    }
  }

export const fetchAssignedCaregivers =
  (config = {}) =>
  async (dispatch) => {
    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_ASSIGNED_CAREGIVERS,
          ...config
        }
      })

      dispatch(setAssignedCaregivers(items))

      return {}
    } catch (err) {
      return catchError(err)
    }
  }

export const saveAssignedCaregiver =
  (config = {}) =>
  async (dispatch, getState) => {
    try {
      const { payload = {}, params = {} } = config

      const { isFirstAssignment } = await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATCH_ASSIGNED_CAREGIVER,
          params
        },
        payload
      })

      showSuccessModal({
        content: ASSIGNED_CAREGIVER_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      if (isFirstAssignment) {
        const patientUuid = patientUuidSelector(getState())

        await dispatch(getPatientProfile(patientUuid))
      }

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updateAssignedCaregiver =
  (config = {}) =>
  async () => {
    try {
      const { payload = {}, params = {} } = config

      await httpClient.request({
        method: 'patch',
        url: {
          pathname: POST_PATCH_ASSIGNED_CAREGIVER,
          params
        },
        payload
      })

      showSuccessModal({
        content: ASSIGNED_CAREGIVER_UPDATE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deleteAssignedCaregiver =
  (config = {}) =>
  async () => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: DELETE_ASSIGNED_CAREGIVER,
          ...config
        }
      })

      showSuccessModal({
        content: ASSIGNED_CAREGIVER_DELETE_MESSAGE,
        centered: true,
        keyboard: false
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchAvailableCaregiverList =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_AVAILABLE_CAREGIVER_LIST,
          ...config
        }
      })

      dispatch(setAvailableCaregivers(items))

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const getPatientAlertNote = (uuid) => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_PATIENT_ALERT_NOTE,
        params: { uuid }
      }
    })

    dispatch(setPatientAlertNote(items))
  } catch (err) {
    catchError(err)
  }
}

export const savePatientAlertNote = (payload) => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'post',
      url: {
        pathname: SAVE_PATIENT_ALERT_NOTE
      },
      payload
    })

    dispatch(setPatientAlertNote(items))
  } catch (err) {
    catchError(err)
  }
}

export const updatePatientAlertNote = (payload, uuid) => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'patch',
      url: {
        pathname: UPDATE_PATIENT_ALERT_NOTE,
        params: { uuid }
      },
      payload
    })

    dispatch(setPatientAlertNote(items))
  } catch (err) {
    catchError(err)
  }
}

export const deletePatientAlertNote = (uuid) => async (dispatch) => {
  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: DELETE_PATIENT_ALERT_NOTE,
        params: { uuid }
      }
    })

    dispatch(setPatientAlertNote({}))
  } catch (err) {
    catchError(err)
  }
}

export const exportPatientMedicalEquipment = async (config = {}) => {
  await exportReport(
    config,
    `patient_medical_equipment_export_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.xls`
  )
}

export const getPatientGeneratePhone =
  (config = {}) =>
  async () => {
    const { params } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_PATIENT_GENERATE_PHONE,
          params
        }
      })
    } catch (err) {
      catchError(err)
    }
  }

export const updatePatientSpecialFieldsRequest = async ({
  params,
  payload = {}
}) => {
  try {
    return await httpClient.request({
      method: 'patch',
      url: {
        pathname: UPDATE_PATIENT_SPECIAL_FIELDS,
        params
      },
      payload
    })
  } catch (err) {
    catchError(err)
    return null
  }
}

export const updatePatientSpecialFields =
  (config = {}, shouldRemoveDeviceId = false) =>
  async (dispatch) => {
    const { payload = {}, params = {} } = config

    const data = await updatePatientSpecialFieldsRequest({ params, payload })

    if (!isEmpty(data) && shouldRemoveDeviceId) {
      await dispatch(
        removeDeviceId(get(params, ['patientUuid'], null), false, false)
      )
    }

    dispatch(setPatientProfile(data))

    return data
  }

export const updatePatientCompanyDivision =
  (config = {}) =>
  async (dispatch) => {
    const { payload = {}, params = {} } = config

    try {
      const patientProfile = await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATCH_PATIENT_DEMOGRAPHICS_COMPANY_DIVISION,
          params
        },
        payload
      })

      dispatch(setPatientProfile(patientProfile))

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const exportFaceSheetReport =
  (config = {}) =>
  async () => {
    const updConfig = {
      pathname: PATIENT_DEMOGRAPHICS_GET_FACESHEET_REPORT,
      ...config
    }
    const fileName = `face_sheet_report_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.pdf`

    await exportReport(updConfig, fileName)
  }

export const exportFaceSheetReportBatch = async (config = {}) => {
  try {
    const { query = {} } = config

    await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_DEMOGRAPHICS_GET_FACESHEET_REPORT_BATCH,
        query
      }
    })

    showSuccessModal({
      content: REPORT_SUCCESS_UPLOAD_MESSAGE,
      centered: true
    })
  } catch (err) {
    catchError(err)
  }
}

export const getPatientReferrals = async (uuid) => {
  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_REFERRALS,
        params: { uuid }
      }
    })
  } catch (err) {
    catchError(err)

    return Promise.reject(new Error('error'))
  }
}
